import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router'
import {
  ChevronUp,
  ClipboardCheck,
  FileText,
  Fingerprint,
  Landmark,
  LogOut,
  Megaphone,
  Ship,
  UserRound,
  Users,
  Warehouse,
  Wrench,
} from 'lucide-react'
import wavesLogo from '@/assets/waves.svg'
import { useIsAdmin, useLogout, useMe } from '@/features/auth/hooks'
import { landingRouteFor } from '@/features/auth/guards'
import { FLOW_ROUTES, type Flow } from '@/lib/flows'

// Navigation items array, one per flow. The dashboard is reachable via the
// FlowPilot logo in the sidebar header (admins only), so it has no nav entry.
const navItems: { flow: Flow; title: string; icon: typeof Users }[] = [
  { flow: 'sales', title: 'Sales', icon: Users },
  { flow: 'inventory', title: 'Inventory', icon: Ship },
  { flow: 'storage', title: 'Storage', icon: Warehouse },
  { flow: 'service', title: 'Service', icon: Wrench },
  { flow: 'inspection', title: 'Inspection', icon: ClipboardCheck },
  { flow: 'marketing', title: 'Marketing', icon: Megaphone },
  { flow: 'finance', title: 'Finance', icon: Landmark },
  { flow: 'reports', title: 'Reports', icon: FileText },
]

// Role badge text: the role's own name for account users, fixed label for
// support staff.
function roleLabel(me?: { role: string; role_name?: string }) {
  if (!me) return ''
  if (me.role === 'fp_admin') return 'FlowPilot Support'
  return me.role_name || '—'
}

export function AppSidebar() {
  const matchRoute = useMatchRoute()
  const navigate = useNavigate()
  // On mobile the sidebar renders inside a Sheet; close it after tapping a
  // nav link so the overlay doesn't linger over the destination page.
  const { isMobile, setOpenMobile } = useSidebar()
  const { data: me } = useMe()
  const logout = useLogout()
  const isAdmin = useIsAdmin()

  // Custom roles only see the flows they were granted; admins/fp-admins see
  // everything (their session carries every flow).
  const items = navItems.filter((item) => !!me?.flows.includes(item.flow))
  // The logo is the dashboard link for admins; custom roles land on their
  // first allowed flow instead (the dashboard is admin-only).
  const homeTo = (me ? landingRouteFor(me) : '/') as never

  const onLogout = () => {
    logout.mutate(undefined, {
      onSettled: () => {
        setOpenMobile(false)
        navigate({ to: '/login' })
      },
    })
  }

  return (
    <Sidebar collapsible="offcanvas">
      <SidebarHeader className="p-4">
        <Link
          to={homeTo}
          className="flex items-center gap-2 transition-opacity hover:opacity-80"
          aria-label="FlowPilot home"
          onClick={() => isMobile && setOpenMobile(false)}
        >
          <img src={wavesLogo} alt="" className="h-10 w-10" />
          <span className="text-lg font-bold text-foreground">FlowPilot</span>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="px-0 py-2">
          <SidebarGroupLabel className="px-4">Flows</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => {
                const to = FLOW_ROUTES[item.flow] as never
                const isActive = !!matchRoute({ to, fuzzy: true })

                return (
                  <SidebarMenuItem key={item.flow}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      className="h-11 w-full gap-3 px-3 text-base [&>svg]:size-5"
                    >
                      <Link to={to} onClick={() => isMobile && setOpenMobile(false)}>
                        <item.icon className="h-5 w-5" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg">
                  <UserRound className="h-5 w-5" />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {me?.display_name || me?.username || '…'}
                    </span>
                    <span className="text-muted-foreground truncate text-xs">
                      {roleLabel(me)}
                      {me?.kind === 'fp_admin'
                        ? ''
                        : me?.account_slug
                          ? ` · ${me.account_slug}`
                          : ''}
                    </span>
                  </div>
                  <ChevronUp className="ml-auto h-4 w-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" className="w-[--radix-dropdown-menu-trigger-width]">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span className="truncate">{me?.username || 'Signed in'}</span>
                  {me && <Badge variant="secondary">{roleLabel(me)}</Badge>}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isAdmin && (
                  <DropdownMenuItem asChild>
                    <Link to="/settings/account" onClick={() => isMobile && setOpenMobile(false)}>
                      <Users className="mr-2 h-4 w-4" />
                      Account settings
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/settings/security" onClick={() => isMobile && setOpenMobile(false)}>
                    <Fingerprint className="mr-2 h-4 w-4" />
                    User settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} disabled={logout.isPending}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
